'use client';

/**
 * ScrollCueButton — pulsante "Scorri" con 3 chevron lampeggianti.
 * Riusabile tra snap section. Click → scrollIntoView del target id,
 * oppure router.push se passato href (per nav a route esterna).
 *
 * direction:
 *  - 'down' → chevron giù, label "Scorri" (default)
 *  - 'up'   → chevron su, label "Su"
 *
 * tone:
 *  - 'dark'  → chevron bianchi (su bg scuro/hero)
 *  - 'light' → chevron scuri (su bg chiaro)
 */

import { useRouter } from 'next/navigation';
import { ChevronDown, ChevronUp } from 'lucide-react';

interface Props {
  targetId?: string;
  href?: string;
  label?: string;
  direction?: 'up' | 'down';
  tone?: 'dark' | 'light';
  className?: string;
}

export default function ScrollCueButton({
  targetId,
  href,
  label,
  direction = 'down',
  tone = 'dark',
  className = '',
}: Props) {
  const router = useRouter();
  const Icon = direction === 'up' ? ChevronUp : ChevronDown;
  const computedLabel = label ?? (direction === 'up' ? 'Su' : 'Scorri');

  const colors = tone === 'dark'
    ? { text: 'text-white/60', textHover: 'group-hover:text-white/90', chev: 'text-white/70', chevHover: 'group-hover:text-white', hover: 'hover:bg-white/5' }
    : { text: 'text-slate-500', textHover: 'group-hover:text-slate-900', chev: 'text-slate-500', chevHover: 'group-hover:text-slate-900', hover: 'hover:bg-slate-900/5' };

  const handleClick = () => {
    if (href) {
      router.push(href);
      return;
    }
    if (targetId) {
      const el = document.getElementById(targetId);
      el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Order chevrons: in direction='up' label below frecce (frecce in alto, label sotto)
  const isUp = direction === 'up';

  return (
    <button
      type="button"
      data-cid={`scroll-cue-${direction}-${targetId || href || ''}`}
      aria-label={`Scorri a ${targetId || href || ''}`}
      onClick={handleClick}
      className={`group flex flex-col items-center gap-1 px-3 py-2 rounded-xl ${colors.hover} transition-colors active:scale-95 ${className}`}
    >
      {!isUp && (
        <span className={`text-[9px] font-bold uppercase tracking-[0.3em] ${colors.text} ${colors.textHover} transition-colors`}>
          {computedLabel}
        </span>
      )}
      <Icon size={16} strokeWidth={2} className={`${colors.chev} ${colors.chevHover} animate-pulse transition-colors`} style={{ animationDuration: '2.5s', animationDelay: '0ms' }} />
      <Icon size={16} strokeWidth={2} className={`-mt-2 ${colors.chev} ${colors.chevHover} animate-pulse transition-colors`} style={{ animationDuration: '2.5s', animationDelay: '300ms' }} />
      <Icon size={16} strokeWidth={2} className={`-mt-2 ${colors.chev} ${colors.chevHover} animate-pulse transition-colors`} style={{ animationDuration: '2.5s', animationDelay: '600ms' }} />
      {isUp && (
        <span className={`text-[9px] font-bold uppercase tracking-[0.3em] ${colors.text} ${colors.textHover} transition-colors`}>
          {computedLabel}
        </span>
      )}
    </button>
  );
}
